import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import ModalView from 'components/CommonUI/ModalView'
import A from 'components/A'
import Type from 'components/Type'
import Icon from 'components/Icon'
import Divider from 'components/Divider'
import TextButton from 'components/TextButton'
import Stack from '@mui/material/Stack'
import { isEmpty } from 'lodash'
import {
  CREATE_PLAID_LINK_TOKEN,
  RELINK_PLAID,
  GET_PLAID_ACCESS_TOKEN,
  GET_AUTHORIZATION_TEXT,
  COMPLETE_ACCOUNT,
} from 'api/constants'
import { CDN, DONDE } from 'utils/constants'
import { usePlaidLink } from 'react-plaid-link'
import { useTypeLoad } from 'utils/hooks'
import { useAction } from 'utils/actions'

const ErrorModal = () => (
    <ModalView sizeX="md">
      <Stack display="grid">
        <Stack display="grid" justifyItems="center" gap={3} py={3} px={2}>
          <Icon icon="Error" />
          <Stack display="grid" gap={1}>
            <Type variant="sectionLabel" textAlign="center">
              Uh oh!
            </Type>
            <Type variant="data" textAlign="center">
              It looks like something went wrong.
              <br />
              Refresh and try again or contact support.
            </Type>
          </Stack>
        </Stack>
        <Divider color="neutralCool_200" />
        <Stack display="grid" gap={3} textAlign="center" py={2.5} px={2}>
          <A
            href={DONDE.LINKS.SUPPORT}
            wrapComponent
          >
            <TextButton>
              Contact customer service
            </TextButton>
          </A>
        </Stack>
      </Stack>
    </ModalView>
  )

const PlaidLinkHelper = ({
  selector,
  accountID,
  fixAccount,
  isCorporate,
  ...props
}) => {
  const dispatch = useDispatch()
  const { openModal, clearFromState } = useAction()
  const plaidLinkToken = useSelector(({ global }) => global[selector]) || {}
  const authorizationText = useSelector(({ global }) => global.authorizationText) || {}

  const onSuccess = (token, metadata) => {
    if (accountID && !fixAccount) {
      dispatch({
        type: COMPLETE_ACCOUNT,
        id: accountID,
        isCorporate: isCorporate,
      })
    } else if (!fixAccount) {
      dispatch({
        type: GET_PLAID_ACCESS_TOKEN,
        data: metadata,
        auth_id: authorizationText?.authorization_id,
        isCorporate: isCorporate,
      })
    }
    props.onSuccess()
    clearFromState([selector])
  }

  const onExit = (error, metadata) => {
    clearFromState([selector])
    props.onExit()
  }

  const onEvent = (...rest) => {
    
  }

  const config = {
    token: plaidLinkToken?.link_token,
    clientName: CDN.PLAID.CLIENT_NAME,
    env: CDN.PLAID.ENV,
    product: CDN.PLAID.PRODUCT,
    // receivedRedirectUri: window.location.href,
    // receivedRedirectUri: 'https://dev.local.me:3000',
    onSuccess,
    onExit,
    onEvent,
  }

  if (accountID) delete config.product

  const { open, ready, error } = usePlaidLink(config)

  useEffect(() => {
    if (ready && !error) open()
  }, [ready])

  useEffect(() => {
    if (error) openModal(<ErrorModal />)
  }, [error])

  useEffect(() => {
    if (isEmpty(authorizationText)) dispatch({ type: GET_AUTHORIZATION_TEXT })
  }, [])

  return null
}

const Link = props => {
  const dispatch = useDispatch()
  const {
    onLoad,
    isCorporate,
    accountID,
    fixAccount,
    onSuccess,
    onExit,
    returnPage,
    children,
  } = props
  const selector = `plaid${isCorporate ? 'Corporate' : ''}${accountID ? 'Relink' : ''}LinkToken`
  const plaidLinkToken = useSelector(({ global }) => global[selector]) || {}
  const { link_token } = plaidLinkToken
  const authorizationText = useSelector(({ global }) => global.authorizationText) || {}
  const { authorization_id } = authorizationText
  const [isClicked, setIsClicked] = useState(false)
  const { clearFromState } = useAction()

  const createPlaidLinkToken = () => {
    clearFromState([selector])
    setIsClicked(true)
    dispatch({
      type: accountID ? RELINK_PLAID : CREATE_PLAID_LINK_TOKEN,
      isCorporate,
      accountID,
      fixAccount,
    })
  }

  useEffect(() => {
    if (onLoad) createPlaidLinkToken()
  }, [onLoad])

  useEffect(() => {
    if (link_token && authorization_id) {
      window.localStorage?.setItem('link_token', link_token)
      if (!!isCorporate) {
        window.localStorage?.setItem('link_token_is_corporate', true)
      }
      window.localStorage?.setItem('link_token_return_pathname', returnPage || window.location.pathname)
      window.localStorage?.setItem('link_token_auth_id', authorization_id)
      if (fixAccount) {
        window.localStorage?.setItem('link_token_fix_account', fixAccount)
      }
      if (accountID) {
        window.localStorage?.setItem('link_token_account_id', accountID)
      }
    }
  }, [
    link_token,
    authorization_id,
    isCorporate,
    returnPage,
    fixAccount,
    accountID,
  ])

  useTypeLoad(GET_PLAID_ACCESS_TOKEN, data => {
    if (!data.error) {
      clearFromState([selector])
      onSuccess()
    }
  })

  return (
    <span onClick={createPlaidLinkToken}>
      {!isEmpty(plaidLinkToken) && isClicked && (
        <PlaidLinkHelper
          {...props}
          selector={selector}
        />
      )}
      {children}
    </span>
  )
}

Link.propTypes = {
  onLoad: PropTypes.bool,
  isCorporate: PropTypes.bool,
  accountID: PropTypes.number,
  fixAccount: PropTypes.bool,
  onSuccess: PropTypes.func,
  onExit: PropTypes.func,
  returnPage: PropTypes.string,
}

Link.defaultProps = {
  onLoading: false,
  isCorporate: false,
  onSuccess: () => {},
  onExit: () => {},
}

export default Link
